/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import DisclaimerFooter from "components/DisclaimerFooter"
import { useEffect } from "react"
import { Link } from "react-router"
import { pageMetaData, updateMetaTags } from "utils/metaTags"

const Guide = () => {
  useEffect(() => {
    const { title, description, keywords } = pageMetaData.guide
    updateMetaTags(title, description, keywords)
  }, [])

  return (
    <div className="pt-1 pb-3">
      <h2 className="fs-3 mb-4">Step-by-step Guide</h2>

      <div className="card mb-4">
        <div className="card-header">
          <h3 className="card-title fs-4 mb-0">Step 1: General Parameters</h3>
        </div>
        <div className="card-body">
          <ol>
            <li>
              <strong>Initial Portfolio Total</strong>: Enter the total savings you plan to have at
              the beginning of your retirement &mdash; include all financial assets like brokerage
              accounts, retirement accounts, CDs, money market accounts,{" "}
              <em>but exclude real estate, especially your primary residence</em>. Note this value
              isn&apos;t your current portfolio total, unless you don&apos;t anticipate any change
              to that between now and retirement.
            </li>
            <li>
              <strong>Retirement Start</strong>: When do you plan to retire? (MM/YYYY format) This
              date only matters if you include cash flows &mdash; it&apos;s used to calculate the
              lengths of time between your retirement start and when cash flows begin.
            </li>
            <li>
              <strong>Retirement Horizon</strong>: The anticipated length of retirement in months
              (30 years = 360 months, 25 years = 300 months). The maximum retirement horizon
              currently supported is 730 months.
            </li>
            <li>
              <strong>Final Value Target</strong>: What percentage of your initial portfolio do you
              want left at the end?
              <ul>
                <li>0% = Spend it all.</li>
                <li>25% = Leave something for heirs, for example.</li>
                <li>100% = Never touch principal.</li>
              </ul>
            </li>
            <li>
              <strong>Initial Consumption</strong> (Optional): Your initial per-annum spending in
              today&apos;s dollars.{" "}
              <em>
                Don&apos;t include any cash flows you&apos;ll specify in the Cash Flow section (like
                Social Security)
              </em>
              .
            </li>
            <li>
              <strong>Inflation Rate</strong>: Your estimate of future annual inflation. Since all
              the returns used by simulations are in real (inflation-adjusted) dollars, this only
              affects the results if cash flows are specified in the Cash Flows section.
            </li>
            <li>
              <strong>Allocation Mode</strong>: Choose how your asset allocation changes over time:
              <ul>
                <li>
                  <strong>Static</strong>: Keep the same allocation throughout retirement (we assume
                  monthly rebalancing).
                </li>
                <li>
                  <strong>Glide Path</strong>: Transition at a fixed rate month by month from
                  initial to final allocations (assuming monthly rebalancing to maintain the final
                  allocation thereafter).
                </li>
              </ul>
              <p>
                <strong>Note</strong>: When you select Glide Path, both the initial and final
                allocations will be set to your current static allocation as a starting point.
              </p>
            </li>
            <li>
              <strong>Glide Path Length</strong> (if using Glide Path): How many months to spend
              transitioning between the initial and final allocations.
            </li>
          </ol>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h3 className="card-title fs-4 mb-0">Step 2: Assets</h3>
        </div>
        <div className="card-body">
          <h4 className="fs-5 mb-2">Allocation</h4>
          <p>
            Set how your portfolio is allocated (or for glide paths, set both your initial and final
            allocations). The percentages for the assets must sum to 100%, but note that the
            Fama-French factor loadings don&apos;t contribute to this sum.
          </p>
          <p>
            <strong>Asset Allocation Ranges</strong>: Each asset allocation can range from -50% to
            150%. This allows for modeling short positions (negative values) and leverage (values
            over 100%).
          </p>
          <p>
            {/* AIDEV-NOTE: plain <a>, not <Link> — Link performs client-side nav and doesn't
                scroll to hash fragments; this app has no ScrollRestoration/hash-scroll handling */}
            See our <a href="/methodology/#data-sources">methodology page</a> for details on how
            returns for each asset are constructed.
          </p>
          <p>
            <strong>Expense Ratio</strong>: the weighted average proportional per-annum expense
            ratio of your investment portfolio. Include ETF and mutual fund expense ratios here, and
            also add AUM investment advisor fees if applicable. For example, if you have 60% in a US
            stock fund with 0.03% fees and 40% in a 10-year Treasury fund with 0.05% fees: (0.6 ×
            0.03%) + (0.4 × 0.05%) = 0.038%.
          </p>

          <h4 className="fs-5 mb-2">Fama-French Factors</h4>
          <p className="mb-3">
            These are used to represent the presence of small-cap and value equities in your US
            equity holdings. Please refer to{" "}
            <a href="https://earlyretirementnow.com/2025/06/02/small-cap-value-swr-series-part-62/">
              this post
            </a>{" "}
            from Karsten Jeske, one of the creators of the site (see the{" "}
            <Link to="/about/">About</Link> page), for guidance on how to use Fama-French Factors in
            the Google Sheet that uses the same <Link to="/methodology/">methodology</Link> as this
            site; the instructions are transferrable. For example, if 50% of your portfolio is in US
            Equities, and that comes from holding VTI, you&apos;d set the Small Stocks factor to 50%
            x 12.8% = 6.4% and the Value Stocks factor to 50% x 0.6% = 0.3%. See the ETF Factor
            Exposures spreadsheet mentioned in the blog post for the factor exposures of other
            popular funds.
          </p>
          <p className="mb-4">
            <strong>Factor Loading Ranges</strong>: Both Small Stocks and Value Stocks factor
            loadings can range from -100% to 100%, allowing for negative factor exposures where
            appropriate.
          </p>
          <div className="alert alert-warning">
            <strong>Caution!</strong> This site currently makes the &quot;Raw&quot; assumption for
            Fama-French factors from the aforementioned blog post. This assumption is that the
            effect of various factor weightings seen through long periods of the past, but not in
            recent decades, will reemerge somewhat in the future &mdash; an assumption Karsten
            strongly contests! If you agree with his stance, for now you can reasonably approximate
            it by leaving both the Small Stocks and Value Stocks factor loadings at 0%.
          </div>
          <div className="alert alert-info">
            <strong>💡 Tip</strong>: Although we expect the general historical relationship between
            portfolio allocation and outcome to hold to some degree in the future, the relationship
            is very unlikely to be the same. Bear in mind then the that the more allocations you try
            in search of the best outcome, the more likely it is that you&apos;ll encounter an
            allocation that worked well in the past but won&apos;t in the future. Err on the side of
            simplicity, and investigate outcomes substantially at odds with those predicted by other
            reputable sources.
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h3 className="card-title fs-4 mb-0">Step 3: Adding Cash Flows (Optional)</h3>
        </div>
        <div className="card-body">
          <p>
            The Cash Flows section lets you model, for example, Social Security, pensions, part-time
            work, and major expenses. <em>Enter all amounts in today&apos;s dollars</em>.
          </p>

          <h4 className="fs-5 mb-2">Common Examples</h4>

          <div className="mb-3">
            <strong>Social Security:</strong>
            <ul>
              <li>Amount: 3000 (for $3,000/month)</li>
              <li>Frequency: Monthly</li>
              <li>Inflation Adjusted: ✓ (yes)</li>
              <li>Start: 01/2030 (when you turn 67)</li>
            </ul>
          </div>

          <div className="mb-3">
            <strong>Mortgage Payment:</strong>
            <ul>
              <li>Amount: -2200 (negative for expenses)</li>
              <li>Frequency: Monthly</li>
              <li>Inflation Adjusted: ✗ (no - fixed payment)</li>
              <li>Start: 01/2025</li>
              <li>End: 01/2050 (when paid off)</li>
            </ul>
          </div>

          <div className="mb-3">
            <strong>One-Time Expense:</strong>
            <ul>
              <li>Amount: -50000 (new roof, major medical, etc.)</li>
              <li>Frequency: One-time</li>
              <li>Start: 06/2028</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h3 className="card-title fs-4 mb-0">Step 4: Save Your Plan (Optional)</h3>
        </div>
        <div className="card-body">
          <p>
            Use the &quot;Save/Load Plan&quot; tab to save your parameter settings as an Excel file
            (.xlsx) or load previously saved settings. The saved file can be edited with spreadsheet
            software, but note that some edits may prevent the file from being loaded back into the
            app if they&apos;re not in the expected form (you&apos;ll be notified of any such edits
            when you attempt to load the file).
          </p>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h3 className="card-title fs-4 mb-0">Step 5: Hit Calculate Results!</h3>
        </div>
        <div className="card-body">
          <p>You&apos;ll be taken to the results page once your results are ready.</p>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h3 className="card-title fs-4 mb-0">Results</h3>
        </div>
        <div className="card-body">
          <p>
            The results page contains several tabs analyzing your retirement plan from different
            angles.
          </p>

          <h4 className="fs-5 mb-3">Historical Data Selection</h4>
          <p>
            Use this section to select the retirements simulated by starting year and market
            conditions at their starts. One reason you may wish to do this is to simulate only
            historical retirements with starting conditions similar to those you anticipate at the
            start of your retirement. (See the <Link to="/methodology/">methodology page</Link> for
            more details on how the simulations are conducted.)
          </p>
          <div className="alert alert-info">
            <strong>💡 Tip</strong>: Bear in mind that reducing the number of simulations too much
            could outweigh the advantage of using range limits that are similar to current market
            conditions.
          </div>
          <ul>
            <li>
              <strong>Years</strong>: Filter the retirements simulated by their starting years.
            </li>
            <li>
              <strong>S&P 500 Drawdowns</strong>: Filter the retirements simulated by how far the
              stock market was below its previous peak at their starts.
            </li>
            <li>
              <strong>CAPE Ratios</strong>: Filter retirements simulated by the CAPE ratios at their
              starts. The Early Retirement Now blog has often incorporated CAPE ratios into its
              analyses. A good place to start reading up on their significance is{" "}
              <a href="https://earlyretirementnow.com/2016/12/21/the-ultimate-guide-to-safe-withdrawal-rates-part-3-equity-valuation/">
                The Ultimate Guide to Safe Withdrawal Rates – Part 3: Equity Valuation
              </a>
              .
            </li>
            <li>
              <strong>CAPE Model</strong>: Choose between Shiller or{" "}
              <a
                href="https://earlyretirementnow.com/2022/10/05/building-a-better-cape-ratio/"
                target="_blank"
                rel="noopener noreferrer"
              >
                ERN
              </a>{" "}
              models when filtering by CAPE ratios.
            </li>
          </ul>
          <p>
            For example, if the year range is 1926-2000, the drawdown range is 5%-80%, and the CAPE
            ratio range is 0-20, then the only retirements simulated will be those starting in
            months between 1926 and 2000, where the S&P 500 was initially 5% to 80% below its
            previous all-time high, and the CAPE ratio was initially between 0 and 20.
          </p>

          <h4 className="fs-5 mb-3">Final Values</h4>
          <p>
            Shows the distribution of your portfolio&apos;s final values across the simulated
            retirements. These are real (inflation-adjusted) values, expressed in today&apos;s
            purchasing power — not future nominal dollars.
          </p>

          <h4 className="fs-5 mb-3">Failure Rates</h4>
          <p>
            Shows the percentage of historical simulations where the portfolio&apos;s value did not
            exceed your specified final target by the end of retirement.
          </p>

          <h4 className="fs-5 mb-3">Consumption Amounts</h4>
          <p>
            Shows initial consumption amounts (in dollars) that had various failure rates under
            different market conditions. These represent initial annual spending amounts equal to
            portfolio withdrawals <em>plus any cash flows you specified</em>. For example, for a
            consumption amount of $120k and a total cash flow of +$12k the withdrawal amount is
            $108k.
          </p>

          <h4 className="fs-5 mb-3">Consumption Rates</h4>
          <p>
            Shows initial consumption rates (as percentages of your initial portfolio) that had
            various failure rates under different market conditions. These represent initial
            spending rates equal to your withdrawal rate{" "}
            <em>
              plus the rate of change to your initial portfolio of any cash flows you specified.
            </em>
          </p>

          <h4 className="fs-5 mb-3">About Results</h4>
          <p>Provides in-app explanations of the results.</p>

          <div className="alert alert-info mb-4">
            <strong>Understanding the Results</strong>: For the tabs containing tables, table
            columns give results for retirements that began within particular periods of time and/or
            share initial market conditions, i.e. with certain historical data selections applied.
            For example, a column header of &quot;CAPE &lt; 20&quot; indicates results derived
            exclusively from simulated retirements where the CAPE ratio in their starting months was
            below 20. The column entitled &quot;Your Filters&quot; shows results for the simulations
            you select in the Historical Data Selection section.
          </div>
        </div>
      </div>

      <h2 className="fs-4 mb-3">Need Help?</h2>
      <ul>
        <li>
          Check our <Link to="/methodology/">Methodology</Link> page for technical details.
        </li>
        <li>
          Read the <Link to="/about/">About</Link> page to find out about the people and motivation
          behind this site.
        </li>
        <li>
          Email us at{" "}
          <a href="mailto:support@saferetirementspending.com">support@saferetirementspending.com</a>
        </li>
      </ul>

      <h2 className="fs-4 mb-3">Ready to Start?</h2>
      <p>
        <Link to="/">Go to the Calculator</Link> and follow this guide. Start simple, then add
        complexity as you get comfortable with the tool!
      </p>

      <DisclaimerFooter />
    </div>
  )
}

export default Guide
